import type { RegistrationId } from '../../changes/ids';
import type { PendingRegistrationChanges } from '../../changes/PendingChangesStore';
import { DICOMApprovalStatus } from '../../dicom/dicomConstants';
import { mat4ToRowMajorArray } from '../../dicom/utils';
import { domainTrace } from '../../diagnostics/domainTrace';
import type { Patient } from '../../patient/patient';
import type { DatasetSaver, SaveScope } from '../save/DatasetSaver';
import { deleteDatasetRow, inScope, mapSaveErrors } from '../save/DatasetSaver';
import type { SaveEffect, SaveEntryResult } from '../save/SaveEffect';
import type { SaveOutcome } from '../save/SaveOutcome';
import type { RigidRegistration } from '../../patient/rigidRegistration';

import type {
    SealedRegistration,
    SealedSaveIntent,
} from '../save/SealedSaveIntent';
import type {
    ConnectorVersionToken,
    PatientPersistenceRef,
} from '../../persistence/PersistenceRef';
import type { PersistenceRefReader } from '../../persistence/PersistenceRegistry';
import type { RegistrationPersistenceGateway } from '../../persistence/RegistrationPersistenceGateway';
import type { PersistedApprovalStatus } from '../../persistence/StructureSetPersistenceGateway';

/** Maps the domain approval enum onto the approval endpoint's wire values. */
function toWireApproval(status: DICOMApprovalStatus): PersistedApprovalStatus {
    switch (status) {
        case DICOMApprovalStatus.APPROVED:
            return 'Approved';
        case DICOMApprovalStatus.REJECTED:
            return 'Rejected';
        default:
            return 'Unapproved';
    }
}

/**
 * The client REG dataset's Study Instance UID (stamped from the primary
 * image at creation) — required by the server create so the new row lands
 * in the right study.
 */
function readStudyInstanceUid(reg: RigidRegistration): string | null {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const uid = (reg.dicomParent.datasetWrapper.dataset as any)
        .StudyInstanceUID;
    return typeof uid === 'string' && uid !== '' ? uid : null;
}

/**
 * Persists pending registration changes to the connector database. Driven
 * by the {@link PatientSaveService} orchestrator (which owns the save
 * attempt); entries are processed in stable id order, each independently.
 *
 * - Deleted: DELETE by dataset id (the server refuses an approved row
 *   with the approved-lock 409 → reload-only conflict).
 * - New: POST create — the SERVER writes the REG file and mints all DICOM
 *   identity. The local dataset keeps its client-minted UIDs on purpose:
 *   the engine's undo service and selection state key registrations by
 *   SOP UID, so rewriting it mid-session would orphan them, and a reload
 *   re-parses the server file anyway. The row ref (dataset id + version)
 *   is what every subsequent save call uses. If a previous attempt left a
 *   ref behind (create succeeded, a later step failed), the create is
 *   skipped and the matrix is re-PUT instead so edits made in between are
 *   never lost.
 * - Modified: unapprove-first when the approval left APPROVED and the
 *   matrix or label also changed (the server copy would 409 those calls),
 *   then the label rename PATCH (row display metadata only — the stored
 *   REG file is untouched), then the matrix PUT (server rewrites the file
 *   under a new SOP instance), then the approval transition. Description
 *   changes are reported as failed — there is no description endpoint in
 *   this release; the entry stays pending rather than being silently
 *   dropped.
 *
 * Wire matrix = `mat4ToRowMajorArray(transformation)` with
 * fixed = sourceFrameOfReferenceUid, moving = targetFrameOfReferenceUid
 * (the viewer's naming is inverted from DICOM's; the bytes are identical
 * to what the client's own REG file sync writes).
 */
export class RegistrationSaveService implements DatasetSaver {
    readonly entity = 'registration' as const;

    constructor(
        private readonly refs: PersistenceRefReader,
        private readonly gateway: RegistrationPersistenceGateway
    ) {}

    /**
     * Captures the transform of every registration in this batch, SYNCHRONOUSLY.
     *
     * `RigidRegistration.transformation` is a live mat4 that the interaction
     * layer writes into on every pointer-move, committing only on release, and
     * nothing disables the registration tools while a save is running. Reading
     * it at network time — after the awaits of the image-upload and
     * structure-set stages, which can run for seconds — therefore persists
     * whatever mid-gesture transform the user happens to be dragging through,
     * as though they had committed it. This is the same hazard the structure-set
     * `prepare` exists for: the bytes that get saved must be the ones the user's
     * intent was captured from.
     *
     * MUST be called with no await between the engine flush and this call.
     */
    seal(
        patient: Patient,
        entries: readonly PendingRegistrationChanges[]
    ): ReadonlyMap<RegistrationId, SealedRegistration> {
        const sealed = new Map<RegistrationId, SealedRegistration>();
        for (const entry of entries) {
            const reg =
                patient.registrations.find(
                    (candidate) => candidate.id === entry.registrationId
                ) ?? null;
            if (!reg) {
                continue; // Reported as failed in the per-entry pass.
            }
            sealed.set(entry.registrationId, {
                matrixRowMajor: mat4ToRowMajorArray(reg.transformation),
                contentLabel: reg.contentLabel,
                contentDescription: reg.contentDescription,
                approvalStatus: reg.approvalStatus,
                reviewerName: reg.reviewerName,
                sourceFrameOfReferenceUid: reg.sourceFrameOfReferenceUid,
                targetFrameOfReferenceUid: reg.targetFrameOfReferenceUid,
                studyInstanceUid: readStudyInstanceUid(reg),
            });
        }
        return sealed;
    }

    async saveEntries(
        sealed: SealedSaveIntent,
        patientRef: PatientPersistenceRef,
        overwrite: ReadonlySet<string> | null,
        scope: SaveScope = 'all'
    ): Promise<SaveEntryResult[]> {
        const ordered = [...sealed.intent.state.byRegistration.values()]
            .filter((entry) => inScope(entry.status, scope))
            .sort((a, b) => a.registrationId.localeCompare(b.registrationId));
        const results: SaveEntryResult[] = [];
        for (const entry of ordered) {
            results.push(
                await this.saveOne(
                    entry,
                    patientRef,
                    overwrite?.has(entry.registrationId) ?? false,
                    sealed.registrations.get(entry.registrationId) ?? null
                )
            );
        }
        return results;
    }

    private async saveOne(
        entry: PendingRegistrationChanges,
        patientRef: PatientPersistenceRef,
        overwrite: boolean,
        sealed: SealedRegistration | null
    ): Promise<SaveEntryResult> {
        const id = entry.registrationId;
        domainTrace.service('RegistrationSaveService.saveOne', {
            id,
            status: entry.status,
        });
        // Owned OUTSIDE the error mapping: a create that committed before
        // a later step threw has already earned its effects, and they
        // must reach the orchestrator with the failed outcome or the
        // retry duplicates the row.
        const effects: SaveEffect[] = [];
        const outcome = await mapSaveErrors(this.entity, id, async () => {
            if (entry.status === 'deleted') {
                // Nothing sealed for a deletion — there is nothing to
                // transmit but the dataset id the registry holds. The
                // server refuses an approved row with the approved-lock
                // 409, which surfaces as a reload-only conflict.
                return deleteDatasetRow(
                    this.refs,
                    this.entity,
                    id,
                    (ref) => this.gateway.deleteRegistration(ref),
                    effects
                );
            }
            // The seal ran in the no-await window; an entry it could not
            // capture means the registration was already gone from the
            // session when the save began. There is no live fallback by
            // design — a read taken now could differ from what the user
            // reviewed.
            if (!sealed) {
                return {
                    kind: 'failed',
                    entity: 'registration',
                    id,
                    message: 'Registration no longer exists in the session',
                };
            }
            switch (entry.status) {
                case 'new':
                    return await this.saveNew(
                        entry,
                        patientRef,
                        overwrite,
                        sealed,
                        effects
                    );
                case 'modified':
                    return await this.saveModified(
                        entry,
                        overwrite,
                        sealed,
                        effects
                    );
            }
        });
        return { outcome, effects };
    }

    private async saveNew(
        entry: PendingRegistrationChanges,
        patientRef: PatientPersistenceRef,
        overwrite: boolean,
        sealed: SealedRegistration,
        effects: SaveEffect[]
    ): Promise<SaveOutcome> {
        const id = entry.registrationId;

        let ref = this.refs.getRef(id);
        if (!ref) {
            const studyInstanceUid = sealed.studyInstanceUid;
            if (!studyInstanceUid) {
                return {
                    kind: 'failed',
                    entity: 'registration',
                    id,
                    message: 'Registration has no study identity',
                };
            }
            const detail = await this.gateway.create({
                patientId: patientRef.patientId,
                studyInstanceUid,
                // The viewer's naming is inverted from DICOM's: the local
                // "source" frame is the REG's top-level (fixed) FOR and
                // "target" is the moving frame the matrix maps to it.
                fixedFrameOfReferenceUid: sealed.sourceFrameOfReferenceUid,
                movingFrameOfReferenceUid: sealed.targetFrameOfReferenceUid,
                matrixRowMajor: sealed.matrixRowMajor,
                label: sealed.contentLabel || null,
            });
            ref = {
                datasetId: detail.datasetId,
                version: detail.version,
            };
            effects.push({ kind: 'setRef', id, ref });
            // The create response carries the whole server-minted identity,
            // not just the row id: a registration created and saved
            // in-session would otherwise export under its locally minted
            // Study/Series/SOP UIDs and name a different instance than the
            // stored file. Adoption is a returned effect; the applier skips
            // a registration deleted during the save.
            effects.push({
                kind: 'adoptRegistrationFileIdentity',
                id,
                identity: detail,
            });
        } else {
            // Retry-safety: a previous attempt created the row and failed
            // later. Mirror saveModified — label first, then matrix, chaining
            // the version — so neither a rename nor a move made between the
            // attempts is silently lost, and an acknowledged overwrite uses a
            // live token rather than the one that just conflicted.
            let version = ref.version;
            if (overwrite) {
                const fresh = await this.gateway.getDetail(ref.datasetId);
                if (fresh.approvalStatus === 'Approved') {
                    return {
                        kind: 'conflict',
                        entity: 'registration',
                        id,
                        reason: 'approvedOnServer',
                    };
                }
                version = fresh.version;
                effects.push({ kind: 'updateVersion', id, version });
            }

            // The create carried the label, but a rename since then has no
            // other way in: a 'new' entry never reports labelDirty. The retry
            // path is rare, so re-sending it unconditionally is cheaper than
            // losing it.
            if (sealed.contentLabel) {
                const renamed = await this.gateway.updateLabel(
                    ref.datasetId,
                    version,
                    sealed.contentLabel
                );
                version = renamed.version;
                effects.push({ kind: 'updateVersion', id, version });
            }

            const detail = await this.gateway.updateMatrix(
                ref.datasetId,
                version,
                sealed.matrixRowMajor
            );
            effects.push({
                kind: 'updateVersion',
                id,
                version: detail.version,
            });
            if (detail.sopInstanceUid) {
                // A returned effect; see the create branch above.
                effects.push({
                    kind: 'adoptRegistrationFileIdentity',
                    id,
                    identity: detail,
                });
            }
            ref = { datasetId: ref.datasetId, version: detail.version };
        }

        // A locally approved/rejected new registration: approval is
        // row-only state (the REG file has no approval attribute).
        if (sealed.approvalStatus !== DICOMApprovalStatus.UNAPPROVED) {
            const approved = await this.gateway.setApproval(
                ref.datasetId,
                ref.version,
                toWireApproval(sealed.approvalStatus),
                sealed.reviewerName || null
            );
            effects.push({
                kind: 'updateVersion',
                id,
                version: approved.version,
            });
        }

        return { kind: 'saved', entity: 'registration', id };
    }

    private async saveModified(
        entry: PendingRegistrationChanges,
        overwrite: boolean,
        sealed: SealedRegistration,
        effects: SaveEffect[]
    ): Promise<SaveOutcome> {
        const id = entry.registrationId;
        const ref = this.refs.getRef(id);
        if (!ref) {
            return {
                kind: 'failed',
                entity: 'registration',
                id,
                message: 'Modified registration has no database identity',
            };
        }

        let version: ConnectorVersionToken = ref.version;
        if (overwrite) {
            // Acknowledged overwrite: never trust a cached conflict token —
            // fetch the row's live one. An APPROVED server copy is never
            // overwritten (revoking a colleague's approval is not a
            // recoverable mistake).
            const fresh = await this.gateway.getDetail(ref.datasetId);
            if (fresh.approvalStatus === 'Approved') {
                return {
                    kind: 'conflict',
                    entity: 'registration',
                    id,
                    reason: 'approvedOnServer',
                };
            }
            version = fresh.version;
            effects.push({ kind: 'updateVersion', id, version });
        }

        const localStatus = sealed.approvalStatus;

        // Unapprove-first: the user un-approved (or rejected) the
        // registration locally and then moved or renamed it. The server
        // copy is still approved and would 409 the matrix PUT / rename
        // PATCH, so the transition goes first.
        const unapproveFirst =
            entry.approvalDirty &&
            (entry.matrixDirty || entry.labelDirty) &&
            localStatus !== DICOMApprovalStatus.APPROVED;
        if (unapproveFirst) {
            const detail = await this.gateway.setApproval(
                ref.datasetId,
                version,
                toWireApproval(localStatus),
                sealed.reviewerName || null
            );
            version = detail.version;
            effects.push({ kind: 'updateVersion', id, version });
        }

        if (entry.labelDirty) {
            const label = sealed.contentLabel;
            if (!label) {
                return {
                    kind: 'failed',
                    entity: 'registration',
                    id,
                    message:
                        'The registration name is empty and cannot be saved.',
                };
            }
            const renamed = await this.gateway.updateLabel(
                ref.datasetId,
                version,
                label
            );
            version = renamed.version;
            effects.push({ kind: 'updateVersion', id, version });
        }

        if (entry.matrixDirty) {
            const detail = await this.gateway.updateMatrix(
                ref.datasetId,
                version,
                sealed.matrixRowMajor
            );
            version = detail.version;
            effects.push({ kind: 'updateVersion', id, version });
            // The server rewrote the file under a fresh SOP Instance UID, so
            // the dataset an export serializes must carry it — the object's
            // own session identity deliberately does not change (see
            // adoptPersistedFileIdentity). A returned effect, as in saveNew.
            if (detail.sopInstanceUid) {
                effects.push({
                    kind: 'adoptRegistrationFileIdentity',
                    id,
                    identity: detail,
                });
            }
        }

        if (entry.approvalDirty && !unapproveFirst) {
            const detail = await this.gateway.setApproval(
                ref.datasetId,
                version,
                toWireApproval(localStatus),
                sealed.reviewerName || null
            );
            effects.push({
                kind: 'updateVersion',
                id,
                version: detail.version,
            });
        }

        if (entry.descriptionDirty) {
            // No description endpoint in this release (the row stores only
            // the label). The honest per-entry failure keeps the entry
            // pending rather than silently dropping the edit; every step
            // above did persist.
            return {
                kind: 'failed',
                entity: 'registration',
                id,
                message:
                    'Editing the description of a saved registration is ' +
                    'not yet supported by the patient database.',
            };
        }

        return { kind: 'saved', entity: 'registration', id };
    }
}
